 
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProject, getProjectJobs, getProjectProgress, getUsers, exportProjectReportPdf, sendProjectReport, downloadBlob } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { formatAmount, projectStatus, projectStatusLabel } from '../../lib/workStatus';
import { ProjectFinancePanel } from '../../components/ProjectFinancePanel';
import { SendPdfDialog } from '../../components/SendPdfDialog';
import { ProjectStatusStepper } from '../../components/ProjectStatusStepper';
import { ProjectStatusDialog } from '../../components/ProjectStatusDialog';
import { ProjectMapSection } from '../../components/map/ProjectMapSection';
import { toast } from 'sonner';
import {
  ArrowLeft,
  TrendingUp,
  Briefcase,
  CheckCircle,
  Clock,
  AlertCircle,
  MapPin,
  FileText,
  Calendar,
  User as UserIcon,
  Plus,
  ListPlus,
  FileDown,
  Mail,
  RefreshCw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import BulkJobCreation from '../admin/BulkJobCreation';

const ProjectDetail = () => {
  const { t, locale } = useLanguage();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [workProgress, setWorkProgress] = useState(null);
  const [clientEmail, setClientEmail] = useState('');
  const [sendOpen, setSendOpen] = useState(false);
  const [sendInternal, setSendInternal] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  const downloadReport = async (internal) => {
    try {
      const blob = await exportProjectReportPdf(projectId, internal);
      downloadBlob(blob, `objekta_atskaite${internal ? '_ieksejais' : ''}.pdf`);
    } catch (error) {
      toast.error('Neizdevās sagatavot PDF');
    }
  };

  const loadData = useCallback(async () => {
    try {
      const [projectData, jobsData] = await Promise.all([
        getProject(projectId),
        getProjectJobs(projectId)
      ]);
      setProject(projectData);
      setJobs(jobsData);
      if (isAdmin) {
        getProjectProgress(projectId).then(setWorkProgress).catch(() => setWorkProgress(null));
        if (projectData?.client_id) {
          getUsers().then((users) => setClientEmail((users || []).find((u) => u.id === projectData.client_id)?.email || '')).catch(() => {});
        }
      }
    } catch (error) {
      console.error('Failed to fetch project:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refreshData = useCallback(async () => {
    try {
      const [projectData, jobsData] = await Promise.all([
        getProject(projectId),
        getProjectJobs(projectId)
      ]);
      setProject(projectData);
      setJobs(jobsData);
    } catch (error) {
      console.error('Failed to fetch project:', error);
    }
  }, [projectId]);

  const handleBulkJobsCreated = () => {
    setIsBulkModalOpen(false);
    refreshData(); // Refresh data
  };

  const statusConfig = {
    planned: { label: t('portalUi.status.planned', 'Plānots'), color: 'blue', icon: Clock },
    in_progress: { label: t('portalUi.status.inProgress', 'Procesā'), color: 'amber', icon: TrendingUp },
    awaiting_info: { label: t('portalUi.status.awaitingInfo', 'Gaida informāciju'), color: 'purple', icon: AlertCircle },
    submitted: { label: t('portalUi.status.submitted', 'Iesniegts'), color: 'indigo', icon: FileText },
    on_hold: { label: t('portalUi.status.onHold', 'Pauzēts'), color: 'gray', icon: Clock },
    completed: { label: t('portalUi.status.completed', 'Pabeigts'), color: 'green', icon: CheckCircle }
  };

  const projectStatusInfo = projectStatus(project?.status);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 text-center">
        <p className="text-[#2E2E2E]/60">{t('portalUi.shared.projectDetail.notFound', 'Projekts nav atrasts')}</p>
      </div>
    );
  }

  const ProjectStatusIcon = ['completed', 'verified', 'delivered', 'closed'].includes(project.status) ? CheckCircle : project.status === 'in_progress' ? TrendingUp : Clock;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Back Button */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Button
          variant="outline"
          onClick={() => navigate(isAdmin ? '/portal/admin/projects' : '/portal/projects')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('portalUi.shared.projectDetail.back', 'Atpakaļ')}
        </Button>
        {isAdmin && (
          <div className="flex flex-wrap gap-2" data-testid="project-report-actions">
            <Button variant="outline" onClick={() => downloadReport(false)} data-testid="project-report-pdf">
              <FileDown className="w-4 h-4 mr-2" /> Atskaite klientam (PDF)
            </Button>
            <Button variant="outline" onClick={() => downloadReport(true)} data-testid="project-report-pdf-internal">
              <FileDown className="w-4 h-4 mr-2" /> Iekšējā atskaite (PDF)
            </Button>
            <Button onClick={() => { setSendInternal(false); setSendOpen(true); }} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="project-report-send">
              <Mail className="w-4 h-4 mr-2" /> Nosūtīt klientam
            </Button>
          </div>
        )}
      </div>

      {/* Project Header */}
      <Card className="border-2 border-[#1F3D2B]/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-2xl mb-3">{project.name}</CardTitle>
              {project.description && (
                <p className="text-[#2E2E2E]/70 mb-4">{project.description}</p>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                {project.property_name && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#1F3D2B]" />
                    <span><strong>{t('portalUi.shared.projectDetail.propertyLabel', 'Īpašums:')}</strong> {project.property_name}</span>
                  </div>
                )}
                {project.cadastral_number && (
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#1F3D2B]" />
                    <span><strong>{t('portalUi.shared.projectDetail.cadastralLabel', 'Kadastrs:')}</strong> {project.cadastral_number}</span>
                  </div>
                )}
                {project.property_address && (
                  <div className="flex items-center gap-2 col-span-2">
                    <MapPin className="w-4 h-4 text-[#1F3D2B]" />
                    <span><strong>{t('portalUi.shared.projectDetail.addressLabel', 'Adrese:')}</strong> {project.property_address}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex flex-col items-end gap-2">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${projectStatusInfo.className}`} data-testid="project-detail-status-badge">
                <ProjectStatusIcon className="w-5 h-5" />
                <span className="font-semibold">{projectStatusLabel(t, project.status)}</span>
              </div>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => setStatusOpen(true)} data-testid="project-detail-change-status">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Mainīt statusu
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6 rounded-xl border border-[#1F3D2B]/10 bg-[#f8faf8] p-4">
            <ProjectStatusStepper status={project.status} history={project.status_history} t={t} />
          </div>
          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-[#2E2E2E]/60 font-medium">{t('portalUi.shared.projectDetail.progressLabel', 'Kopējais progress')}</span>
              <span className="font-bold text-[#1F3D2B] text-lg">{project.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 shadow-inner">
              <div
                className="bg-gradient-to-r from-[#1F3D2B] via-[#2a5039] to-green-600 h-4 rounded-full transition-all duration-700 shadow-lg"
                style={{ width: `${project.progress}%` }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <Briefcase className="w-6 h-6 text-[#2E2E2E]/40 mx-auto mb-2" />
              <p className="text-2xl font-bold text-[#2E2E2E]">{project.total_jobs}</p>
              <p className="text-xs text-[#2E2E2E]/60">{t('portalUi.shared.projectDetail.jobsTotal', 'Kopā darbi')}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center border border-green-100">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-600">{project.completed_jobs}</p>
              <p className="text-xs text-green-700">{t('portalUi.shared.projectDetail.jobsCompleted', 'Pabeigti')}</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-4 text-center border border-amber-100">
              <TrendingUp className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-600">{project.in_progress_jobs}</p>
              <p className="text-xs text-amber-700">{t('portalUi.shared.projectDetail.jobsInProgress', 'Procesā')}</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
              <Clock className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-blue-600">{project.planned_jobs}</p>
              <p className="text-xs text-blue-700">{t('portalUi.shared.projectDetail.jobsPlanned', 'Plānoti')}</p>
            </div>
          </div>

          {/* Kas notiks tālāk? */}
          {project.next_step && (
            <div
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6"
              data-testid={`project-next-step-${project.id}`}
            >
              <p className="text-sm font-semibold text-blue-900 mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {t('portalUi.shared.projectDetail.nextStepTitle', 'Kas notiks tālāk?')}
              </p>
              <p className="text-base text-blue-800">{project.next_step}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Work progress (admin) */}
      <ProjectMapSection project={project} isAdmin={isAdmin} onProjectChange={(changes) => setProject((prev) => ({ ...prev, ...changes }))} />

      {isAdmin && workProgress && (
        <Card className="border-0 shadow-sm" data-testid="project-work-progress">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#1F3D2B]" /> Darbu izpildes progress</CardTitle>
              <span className="font-bold text-[#1F3D2B]" data-testid="project-work-progress-overall">{workProgress.overall_percent}%</span>
            </div>
            {project.area_ha && <p className="text-sm text-[#2E2E2E]/60">Kopējā platība: {formatAmount(project.area_ha, 'ha')}</p>}
          </CardHeader>
          <CardContent className="space-y-3">
            {workProgress.tasks.length === 0 ? (
              <p className="text-sm text-[#2E2E2E]/60" data-testid="project-work-progress-empty">Šim objektam vēl nav piešķirti darba uzdevumi darbiniekiem.</p>
            ) : workProgress.tasks.map((task) => (
              <div key={task.task_id} data-testid={`project-work-progress-${task.task_id}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">{task.work_type_label || task.title} <span className="text-[#2E2E2E]/50">· {task.employee_name || '-'}</span></span>
                  <span>{formatAmount(task.reported_amount, task.unit)}{task.planned_amount ? ` / ${formatAmount(task.planned_amount, task.unit)}` : ''} — <strong>{task.percent}%</strong></span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-gray-200">
                  <div className={`h-2.5 rounded-full transition-all ${task.percent >= 100 ? 'bg-green-600' : 'bg-[#1F3D2B]'}`} style={{ width: `${task.percent}%` }} />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isAdmin && <ProjectFinancePanel projectId={projectId} />}

      {isAdmin && (
        <ProjectStatusDialog
          open={statusOpen}
          onOpenChange={setStatusOpen}
          project={project}
          t={t}
          onUpdated={(updated) => setProject((prev) => ({ ...prev, ...updated }))}
        />
      )}

      {isAdmin && (
        <SendPdfDialog
          open={sendOpen}
          onOpenChange={setSendOpen}
          title="Nosūtīt darbu atskaiti"
          description={`PDF atskaite par objektu "${project.name}" tiks nosūtīta e-pastā${sendInternal ? ' (iekšējā versija ar izmaksām)' : ' (klienta versija bez likmēm un izmaksām)'}.`}
          defaultEmail={clientEmail}
          onSend={(email, message) => sendProjectReport(projectId, { email, message, internal: sendInternal })}
          testId="project-report-send"
        />
      )}

      {/* Jobs List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-[#2E2E2E] flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#1F3D2B]" />
            {t('portalUi.shared.projectDetail.jobsTitle', 'Darbi projektā')}
          </h2>
          {isAdmin && (
            <Button
              onClick={() => setIsBulkModalOpen(true)}
              className="bg-[#1F3D2B] hover:bg-[#2a5039]"
            >
              <ListPlus className="w-4 h-4 mr-2" />
              {t('portalUi.shared.projectDetail.bulkAddJobs', 'Pievienot vairākus darbus')}
            </Button>
          )}
        </div>
        
        <div className="space-y-3">
          {jobs.map(job => {
            const jobStatus = statusConfig[job.status];
            const JobStatusIcon = jobStatus?.icon || Clock;
            const colorClass = `text-${jobStatus?.color}-600`;
            const bgClass = `bg-${jobStatus?.color}-50`;
            const borderClass = `border-${jobStatus?.color}-200`;
            
            return (
              <Card key={job.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#2E2E2E] mb-1">{job.title}</h3>
                      {job.description && (
                        <p className="text-sm text-[#2E2E2E]/60 mb-2">{job.description}</p>
                      )}
                      
                      <div className="flex flex-wrap gap-3 text-sm">
                        {job.responsible_person && (
                          <span className="flex items-center gap-1 text-[#2E2E2E]/70">
                            <UserIcon className="w-3 h-3" />
                            {job.responsible_person}
                          </span>
                        )}
                        {job.planned_end_date && (
                          <span className="flex items-center gap-1 text-[#2E2E2E]/70">
                            <Calendar className="w-3 h-3" />
                            {t('portalUi.shared.projectDetail.deadlineLabel', 'Termiņš')}: {new Date(job.planned_end_date).toLocaleDateString(locale)}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${bgClass} ${borderClass} border`}>
                      <JobStatusIcon className={`w-4 h-4 ${colorClass}`} />
                      <span className={`text-sm font-medium ${colorClass}`}>
                        {jobStatus?.label}
                      </span>
                    </div>
                  </div>

                  {/* Timeline Preview */}
                  {job.timeline && job.timeline.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-[#2E2E2E]/60 mb-2">{t('portalUi.shared.projectDetail.lastUpdateLabel', 'Pēdējā izmaiņa:')}</p>
                      <p className="text-sm text-[#2E2E2E]">
                        {job.timeline[job.timeline.length - 1].comment || job.timeline[job.timeline.length - 1].status}
                        <span className="text-[#2E2E2E]/60 ml-2">
                          ({new Date(job.timeline[job.timeline.length - 1].date).toLocaleDateString('lv-LV')})
                        </span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {jobs.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-[#2E2E2E]/20" />
            <p className="text-[#2E2E2E]/60">{t('portalUi.shared.projectDetail.emptyJobs', 'Šim projektam vēl nav pievienots neviens darbs')}</p>
            {isAdmin && (
              <Button
                onClick={() => setIsBulkModalOpen(true)}
                className="mt-4 bg-[#1F3D2B] hover:bg-[#2a5039]"
              >
                <Plus className="w-4 h-4 mr-2" />
                Pievienot darbus
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Bulk Job Creation Modal (Admin Only) */}
      {isAdmin && project && (
        <BulkJobCreation
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          projectId={project.id}
          clientId={project.client_id}
          onSuccess={handleBulkJobsCreated}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
